import Vue from 'vue';
import {
  CYCLE_MESSAGES,
  FC_CYCLE_SETTINGS_API,
  FC_PROJECT_API,
  FC_UPSI_API,
  MANAGE_USER_OP,
  MESSAGES
} from '../utils/constants';
import {
  buildAssociateOperations,
  buildManageUserOperation,
  manageCycleUsersRequest
} from '../utils/manageUserApi';
import {
  createInitialCyclesFilter,
  DEFAULT_CYCLES_FILTERS,
} from '../utils/cyclesFilterConstants';
import {
  getApiErrorMessage,
  HTTP_BAD_REQUEST,
  HTTP_CONFLICT,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
} from '@/utils/http';

const ADD_PROJECT_FIELDS = [
  'description',
  'from',
  'to',
  'notification',
  'checklistfrom',
  'cycle_type',
  'location',
  'checklistfromvalue',
];

const EDIT_PROJECT_FIELDS = ['_id', 'description', 'from', 'to', 'notification'];

const UPSI_PATCH_STATUSES = [HTTP_FORBIDDEN, HTTP_NOT_FOUND, HTTP_CONFLICT];
const UPSI_ROW_KEYS = ['stage', 'stage_text', 'upsi_state', 'upsi_permissions'];

function pickAllowedFields(source, fields) {
  const input = source && typeof source === 'object' ? source : {};
  const body = {};
  fields.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      body[key] = input[key];
    }
  });
  return body;
}

/**
 * V2 envelope is `response.data = { status, message, data }`.
 * UPSI writes return `data` (the row patch) on success.
 * HTTP 400 returns the field-error list in `data` (`[{ field, message }]`) and does not throw.
 */
async function postUpsiWrite({ commit }, { url, body, actionName }) {
  try {
    const response = await Vue.axios.post(url, body);
    const envelope = response?.data || {};
    const data = envelope.data;
    const patchable = data && typeof data === 'object' && !Array.isArray(data);
    if (envelope.status !== 'success' || !patchable) {
      throw new Error(envelope.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
    }
    commit('patchProjectUpsi', data);
    if (envelope.message) {
      commit('sbSuccess', envelope.message, { root: true });
    }
    return data;
  } catch (err) {
    console.error(`[project/store]: ${actionName} error`, err);
    const status = err?.response?.status;
    if (status === HTTP_BAD_REQUEST) {
      const fieldErrors = err?.response?.data?.data;
      return Array.isArray(fieldErrors) ? fieldErrors : [];
    }
    if (UPSI_PATCH_STATUSES.includes(status)) {
      const patch = err?.response?.data?.data;
      if (patch && typeof patch === 'object' && !Array.isArray(patch)) {
        commit('patchProjectUpsi', patch);
      }
    }
    commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
    throw err;
  }
}

function projectStatusErrorMessage(err, conflictFallback) {
  const serverMessage = err?.response?.data?.message;
  if (serverMessage) return err?.message || serverMessage;
  if (err?.response?.status === HTTP_CONFLICT) return conflictFallback;
  return err?.message || CYCLE_MESSAGES.ERROR.UNKNOWN;
}

const pendingCycleUserDetails = new Map();

export default {
  namespaced: true,

  state: {
    projectList: [],
    projectListLoading: false,
    projectListError: null,
    cyclesFilter: createInitialCyclesFilter(),
    cycleManagerIds: {},
  },

  mutations: {
    setProjectList(state, projects) {
      state.projectList = projects;
    },
    setProjectListLoading(state, loading) {
      state.projectListLoading = loading;
    },
    setProjectListError(state, error) {
      state.projectListError = error;
    },
    SET_CYCLE_MANAGER_IDS(state, { cycleId, ids }) {
      state.cycleManagerIds = { ...state.cycleManagerIds, [String(cycleId)]: ids || [] };
    },
    setCyclesFilter(state, filters) {
      state.cyclesFilter = {
        ...DEFAULT_CYCLES_FILTERS,
        ...(filters || {}),
        locations: [...((filters && filters.locations) || [])],
        users: [...((filters && filters.users) || [])],
        periods: [...((filters && filters.periods) || [])],
        upsiStatuses: [...((filters && filters.upsiStatuses) || [])],
      };
    },
    patchProjectUpsi(state, payload) {
      const patch = payload && typeof payload === 'object' ? payload : {};
      if (!patch._id) return;
      const item = state.projectList.find((row) => row && row._id === patch._id);
      if (!item) return;
      UPSI_ROW_KEYS.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(patch, key)) {
          Vue.set(item, key, patch[key]);
        }
      });
    },
  },

  getters: {
    projectList: state => state.projectList,
    projectListLoading: state => state.projectListLoading,
    projectListError: state => state.projectListError,
    cyclesFilter: state => state.cyclesFilter,
    cycleManagerIds: (state) => (cycleId) => state.cycleManagerIds[String(cycleId)] || [],
  },

  actions: {
    /**
     * V3: Fetch project list with inline checklist summary.
     * Uses optimised single-aggregation endpoint.
     * @param {{ silent?: boolean }} [options] - When silent, skip projectListLoading (background poll).
     */
    async fetchProjectListV3({ commit, rootState }, { silent = false } = {}) {
      const clientAtStart = rootState && rootState.client != null ? String(rootState.client) : '';
      const stillThisClient = () => String((rootState && rootState.client) || '') === clientAtStart;
      if (!silent) commit('setProjectListLoading', true);
      commit('setProjectListError', null);
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.GET_V3);
        if (!stillThisClient()) return;
        if (response.data.status === 'success' && Array.isArray(response.data.data)) {
          commit('setProjectList', response.data.data);
        } else {
          commit('setProjectList', []);
          commit('setProjectListError', response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
      } catch (err) {
        if (!stillThisClient()) return;
        commit('setProjectList', []);
        commit('setProjectListError', err.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
      } finally {
        if (!silent) commit('setProjectListLoading', false);
      }
    },

    /**
     * Most recent open cycle from the shared lite list (landingCycle).
     * Returns the cycle object or null if none found.
     */
    async fetchMostRecentOpenCycle({ dispatch, rootGetters, rootState }) {
      if (!rootState.client) return null;
      try {
        await dispatch('fetchCyclesForSwitcher', null, { root: true });
        return rootGetters.landingCycle || null;
      } catch (err) {
        console.error('[project/store]: fetchMostRecentOpenCycle error', err);
        return null;
      }
    },

    async getFormData({ commit }) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.GET_FORM_DATA);
        if (response.data.status === 'success') {
          return response.data.data[0] || {};
        }
        throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
      } catch (err) {
        console.error('[project/store]: getFormData error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    async addProject({ commit }, payload) {
      const data = pickAllowedFields(payload, ADD_PROJECT_FIELDS);
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.ADD, { data });
        return response;
      } catch (err) {
        console.error('[project/store]: addProject error', err);
        if (err?.response?.status !== HTTP_BAD_REQUEST) {
          commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        }
        throw err;
      }
    },

    async editProject({ commit }, { id, data }) {
      const body = pickAllowedFields(data, EDIT_PROJECT_FIELDS);
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.EDIT(id), { data: body });
        return response;
      } catch (err) {
        console.error('[project/store]: editProject error', err);
        if (err?.response?.status !== HTTP_BAD_REQUEST) {
          commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        }
        throw err;
      }
    },

    async deleteProject({ commit, dispatch }, id) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.DELETE(id));
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.CYCLE_DELETED, { root: true });
          await dispatch('invalidateCyclesForSwitcher', null, { root: true });
        } else {
          throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: deleteProject error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    async closeProject({ commit, dispatch }, id) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.CLOSE(id));
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.PROJECT_CLOSED, { root: true });
          await dispatch('invalidateCyclesForSwitcher', null, { root: true });
        } else {
          throw new Error(CYCLE_MESSAGES.ERROR.ERROR_CLOSING_PROJECT);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: closeProject error', err);
        commit(
          'sbError',
          projectStatusErrorMessage(err, CYCLE_MESSAGES.ERROR.CYCLE_ALREADY_CLOSED),
          { root: true }
        );
        throw err;
      }
    },

    async openProject({ commit, dispatch }, id) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.OPEN(id));
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.PROJECT_REOPENED, { root: true });
          await dispatch('invalidateCyclesForSwitcher', null, { root: true });
        } else {
          throw new Error(CYCLE_MESSAGES.ERROR.ERROR_REOPENING_PROJECT);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: openProject error', err);
        commit(
          'sbError',
          projectStatusErrorMessage(err, CYCLE_MESSAGES.ERROR.CYCLE_ALREADY_OPEN),
          { root: true }
        );
        throw err;
      }
    },

    async toggleNotification({ commit }, { id, type }) {
      try {
        const api =
          type === 'Disable'
            ? FC_PROJECT_API.DISABLE_NOTIFICATION(id)
            : FC_PROJECT_API.ENABLE_NOTIFICATION(id);
        const response = await Vue.axios.post(api);
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.EMAIL_NOTIFICATION_SUCCESS(type), { root: true });
        } else {
          throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: toggleNotification error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    async generateAIChecklist({ commit }, payload) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.GENERATE_AI_CHECKLIST, {
          data: payload,
        });
        return response;
      } catch (err) {
        console.error('[project/store]: generateAIChecklist error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    async syncERP({ commit }, { payload, id }) {
      try {
        if (!id || !payload.from || !payload.to) {
          throw new Error('Missing project or period dates');
        }
        const body = { project: id, startDate: payload.from, endDate: payload.to };
        const response = await Vue.axios.post(FC_PROJECT_API.ERP_SYNC_TRIAL_BALANCE, body);
        return response;
      } catch (err) {
        console.error('[project/store]: syncERP error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    async checkChartOfAccountsSetup({ commit }) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.CHART_OF_ACCOUNTS_GET);
        if (response.data.status === 'success') {
          return response.data.data.length > 0;
        }
        return false;
      } catch (err) {
        console.error('[project/store]: checkChartOfAccountsSetup error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        return false;
      }
    },

    async retryCycleCreation({ commit }, id) {
      try {
        const response = await Vue.axios.post(FC_PROJECT_API.RETRY_CYCLE_CREATION(id));
        if (response.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.CYCLE_CREATION_RETRY, { root: true });
        } else {
          throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
        return response;
      } catch (err) {
        console.error('[project/store]: retryCycleCreation error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    /**
     * Batch (or sequential) cycle team member ops via manageuser (all-or-nothing).
     * @param {{ cycleId: string, operations?: Array, userIds?: string[], role?: string }} payload
     * @returns {Promise<{ count: number }>}
     */
    async manageCycleUsers({ commit }, payload = {}) {
      const cycleId = payload.cycleId;
      let operations = payload.operations;
      if (!operations && Array.isArray(payload.userIds)) {
        operations = buildAssociateOperations(payload.userIds, payload.role);
      }
      try {
        const result = await manageCycleUsersRequest(Vue.axios, cycleId, operations || []);
        commit('sbSuccess', MESSAGES.TEAM_MEMBERS_ADDED(result.count || 0), { root: true });
        return result;
      } catch (err) {
        console.error('[project/store]: manageCycleUsers error', err);
        commit('sbError', getApiErrorMessage(err, MESSAGES.TEAM_MEMBERS_NONE_ADDED), { root: true });
        throw err;
      }
    },

    /**
     * Single manageuser op (move / remove / replace).
     */
    async manageCycleUserOperation({ commit }, { cycleId, type, userid, usertype, fromuserid, touserid }) {
      const operation = buildManageUserOperation({ type, userid, usertype, fromuserid, touserid });
      const successByType = {
        [MANAGE_USER_OP.ASSOCIATE]: MESSAGES.USER_ASSOCIATED,
        [MANAGE_USER_OP.MOVE]: MESSAGES.USER_CHANGED,
        [MANAGE_USER_OP.REMOVE]: MESSAGES.USER_REMOVED,
        [MANAGE_USER_OP.REPLACE]: MESSAGES.USER_REPLACED
      };
      try {
        await manageCycleUsersRequest(Vue.axios, cycleId, [operation]);
        commit('sbSuccess', successByType[type] || MESSAGES.USER_CHANGED, { root: true });
        return { success: true };
      } catch (err) {
        console.error('[project/store]: manageCycleUserOperation error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    async fetchCycleUserDetails({ commit }, cycleId) {
      const key = String(cycleId || '');
      if (pendingCycleUserDetails.has(key)) {
        return pendingCycleUserDetails.get(key);
      }
      const request = (async () => {
        try {
          const response = await Vue.axios.post(FC_CYCLE_SETTINGS_API.GET_USER_DETAILS(cycleId));
          if (response.data.status !== 'success' || !response.data.data?.length) {
            throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
          }
          const row = response.data.data[0] || {};
          const ids = (row.metadata?.managerlist || row.managerlist || []).map((id) => String(id));
          commit('SET_CYCLE_MANAGER_IDS', { cycleId: key, ids });
          return row;
        } catch (err) {
          console.error('[project/store]: fetchCycleUserDetails error', err);
          commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
          throw err;
        } finally {
          pendingCycleUserDetails.delete(key);
        }
      })();
      pendingCycleUserDetails.set(key, request);
      return request;
    },

    async fetchUsersFormData({ commit }) {
      try {
        const response = await Vue.axios.post(FC_CYCLE_SETTINGS_API.GET_USERS_FORMDATA);
        if (response.data.status !== 'success') {
          throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
        return response.data.data[0] || {};
      } catch (err) {
        console.error('[project/store]: fetchUsersFormData error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    async replaceInfoOwner({ commit }, { pbcProjectId, data }) {
      try {
        const response = await Vue.axios.post(
          FC_CYCLE_SETTINGS_API.MANAGE_INFO_OWNER(pbcProjectId),
          { data: [data] }
        );
        if (response.data.status !== 'success') {
          throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
        commit('sbSuccess', MESSAGES.INFO_OWNER_CHANGED, { root: true });
        return response.data;
      } catch (err) {
        console.error('[project/store]: replaceInfoOwner error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    async fetchCycleThresholds({ commit, rootGetters }, { cycleId, filteredLocations }) {
      try {
        const response = await Vue.axios.post(FC_CYCLE_SETTINGS_API.THRESHOLD_GET(cycleId));
        if (response.data.status !== 'success') {
          throw new Error(response.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
        }
        const record = response.data.data[0] || {};
        let locationListdata = filteredLocations || rootGetters['tasks/getFilteredLocations'] || [];
        if (!locationListdata.length) {
          const locResponse = await Vue.axios.post(FC_CYCLE_SETTINGS_API.LOCATION_GET);
          if (locResponse.data.status === 'success') {
            locationListdata = locResponse.data.data || [];
          }
        }
        return {
          listdata: record.threshold || [],
          metadata: record.metadata || {},
          locationListdata,
          canEditThreshold: record.canEditThreshold === true
        };
      } catch (err) {
        console.error('[project/store]: fetchCycleThresholds error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        throw err;
      }
    },

    async addCycleThreshold({ commit }, { cycleId, data }) {
      try {
        const response = await Vue.axios.post(
          FC_CYCLE_SETTINGS_API.THRESHOLD_ADD(cycleId),
          { data }
        );
        if (response.data.status === 'success') {
          commit('sbSuccess', response.data.message, { root: true });
          return { success: true, fieldErrors: null };
        }
        const responseData = Array.isArray(response?.data?.data) ? response.data.data : [];
        return {
          success: false,
          message: response?.data?.message || 'Failed to add threshold.',
          fieldErrors: responseData[0]?.index0 || null
        };
      } catch (err) {
        console.error('[project/store]: addCycleThreshold error', err);
        const errMessage = getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN);
        commit('sbError', errMessage, { root: true });
        throw err;
      }
    },

    async updateCycleThreshold({ commit }, { thresholdId, data }) {
      try {
        const response = await Vue.axios.post(
          FC_CYCLE_SETTINGS_API.THRESHOLD_EDIT(thresholdId),
          { data }
        );
        if (response.data.status === 'success') {
          commit('sbSuccess', response.data.message, { root: true });
          return { success: true, fieldErrors: null };
        }
        const responseData = Array.isArray(response?.data?.data) ? response.data.data : [];
        return {
          success: false,
          message: response?.data?.message || 'Failed to update threshold.',
          fieldErrors: responseData[0]?.index0 || null
        };
      } catch (err) {
        console.error('[project/store]: updateCycleThreshold error', err);
        const errMessage = getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN);
        commit('sbError', errMessage, { root: true });
        throw err;
      }
    },

    async requestUpsiPublish({ commit }, { id, filingReference, remarks, upsiVersion, publicationAt }) {
      const body = {
        filing_reference: filingReference,
        remarks: remarks == null ? '' : remarks,
        upsi_version: upsiVersion,
        publication_at: publicationAt || new Date().toISOString(),
      };
      return postUpsiWrite({ commit }, {
        url: FC_UPSI_API.REQUEST(id),
        body,
        actionName: 'requestUpsiPublish',
      });
    },

    async reviewUpsiRequest({ commit }, { id, requestId, decision, remarks, upsiVersion }) {
      const body = {
        request_id: requestId,
        decision,
        remarks: remarks == null ? '' : remarks,
        upsi_version: upsiVersion,
      };
      return postUpsiWrite({ commit }, {
        url: FC_UPSI_API.REVIEW(id),
        body,
        actionName: 'reviewUpsiRequest',
      });
    },

    async fetchUpsiDetails({ commit }, id) {
      try {
        const response = await Vue.axios.post(FC_UPSI_API.DETAILS(id), {});
        const envelope = response?.data || {};
        if (envelope.status === 'success') {
          return envelope.data ?? null;
        }
        throw new Error(envelope.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
      } catch (err) {
        console.error('[project/store]: fetchUpsiDetails error', err);
        commit('sbError', getApiErrorMessage(err, CYCLE_MESSAGES.ERROR.UNKNOWN), { root: true });
        return null;
      }
    },
  },
};
